/* eslint-disable camelcase */
import React, { useState } from 'react';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import Collapse from '@material-ui/core/Collapse';
import { Button, Icon, Radio } from 'fpay-ui';

import { useForm, useRefund, useTransaction } from '@hooks';
import { moneyFormat } from '@helpers';
import * as Routes from '@constants/routes';
import { ERROR_INSUFFICIENT_BALANCE, ERROR_INSUFFICIENT_BALANCE_TOMORROW } from '@hooks/useRefund';
import Input from '@components/Inputs/InputCurrency';
import { getEnv } from '@config';
import ConfirmModal from './ConfirmModal';
import Loader from './Loader';

const { publicPath } = getEnv();

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 32px;
  margin: auto;
  background-color: #fff;
  width: 632px;

  ${({ theme }) => theme.newBreakpoint('new_md')} {
    margin-top: 24px;
  }

  ${({ theme }) => theme.newBreakpoint('new_sm')} {
    width: 100%;
    height: inherit;
  }
`;

const ContainerInput = styled.div`
  display: flex;
  width: 100%;
  margin-top: 16px;
`;

const Title = styled.div`
  font-family: Roboto;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 23px;
  letter-spacing: 0em;
  text-align: left;
`;

const TransactionSection = styled.div`
  margin-top: 24px;
  border-bottom: 1px solid #e4e6eb;
`;

const SubTitle = styled.div`
  height: 16px;
  display: flex;
  justify-content: space-between;
  ${({ theme }) => css`
    ${theme.textTypes['Body-2-w500']}
  `}
`;

const SubTitleOnly = styled(SubTitle)`
  margin: 24px 0px;
`;

const TransactionRow = styled.div`
  height: 16px;
  display: flex;
  justify-content: space-between;
  margin: 8px 0px;
  font-family: Roboto;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
  letter-spacing: 0em;
  text-align: left;
`;

const ButtonList = styled.div`
  margin-top: 40px;
  flex-grow: 1;
  display: flex;
  justify-content: end;
  > button {
    margin-left: 16px;
  }
`;

const StyledButton = styled(Button)`
  ${({ theme }) => theme.breakpoint('md')} {
  }
  padding: 0px;
  width: 160px;
  height: 48px;
  text-align: center;
  ${({ theme }) => theme.newBreakpoint('new_sm')} {
    flex-grow: 1;
  }
`;

const StyledIcon = styled(Icon)(
  ({ open }) => css`
    position: relative;
    width: 20px;
    height: 20px;
    top: 5px;
    margin-left: 3px;
    transition: transform 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;
    ${open &&
    css`
      transform: rotate(180deg);
    `}
  `
);

const StyledRadio = styled(Radio)`
  margin-top: 32px;
  & label {
    display: flex;
    flex-direction: row-reverse;
    span {
      margin-left: 4px;
    }
  }
  & span::before {
    box-sizing: initial;
    top: 2px;
  }
  & span::after {
    box-sizing: initial;
    top: 6px;
  }
`;

const StyledCollapse = styled(Collapse)`
  margin-bottom: 0;
  ${TransactionRow} {
    margin: 0 0 8px 0;
  }
`;

const CollapseText = styled.div(
  ({ theme }) => css`
    ${theme.textTypes['Body-2-w400']}
    color: ${theme.colors.gray600};
    cursor: pointer;
    margin-bottom: 24px;
  `
);

const NUMBER_OF_REFUNDS_VISIBLE_BY_DEFAULT = 3;

function TransactionDetail() {
  const { t } = useTranslation();
  const history = useHistory();
  const {
    isLoading,
    refund,
    errorBalance,
    setErrorBalance,
    transaction,
    refunds,
    originalTransactionSaleAmount: originalTransactionAmount,
  } = useRefund();
  const { refundsSummary } = useTransaction();
  const [open, setOpen] = useState(false);
  const [refundAll, setRefundAll] = useState('0');

  /* TODO: Move this logic */
  if (!Object.keys(transaction).length > 0) {
    history.push(Routes.TRANSACTIONS);
  }

  const totalRefundsAmount = refunds.reduce((acc, elem) => acc + (elem?.gross_value || 0), 0);

  // For some reason we are using this two functions in cojuntion for getting the right sale amount
  const availableTransactionAmount = originalTransactionAmount + totalRefundsAmount;

  const visibleRefunds = refundsSummary.slice(0, NUMBER_OF_REFUNDS_VISIBLE_BY_DEFAULT);
  const hiddenRefunds = refundsSummary.slice(NUMBER_OF_REFUNDS_VISIBLE_BY_DEFAULT);

  const { formValues, handleInputChange, updateValue, isValid, errors } = useForm({
    refund_amount: 0,
    max_refund_amount: availableTransactionAmount,
    original_transaction_amount: originalTransactionAmount,
  });

  let textErrorBalance = null;
  if (errorBalance === ERROR_INSUFFICIENT_BALANCE) {
    textErrorBalance = [
      t('transactions.detail.errorBalanceSettledSubTitle1'),
      <br />,
      t('transactions.detail.errorBalanceSettledSubTitle2'),
    ];
  } else if (errorBalance === ERROR_INSUFFICIENT_BALANCE_TOMORROW) {
    textErrorBalance = [
      t('transactions.detail.errorBalanceSettledTomorrowSubTitle1'),
      <br />,
      t('transactions.detail.errorBalanceSettledTomorrowSubTitle2'),
    ];
  }
  const handleRefund = () => {
    if (!isValid()) return '';
    refund(formValues.refund_amount);
  };

  const handleConfirm = (losingFocus) => {
    if (losingFocus === true) {
      setErrorBalance(null);
    } else {
      history.push(Routes.TRANSACTIONS);
    }
  };

  const handleRefundAll = () => {
    setRefundAll((prevState) => (prevState === '0' ? '1' : '0'));
    updateValue('refund_amount', refundAll === '0' ? availableTransactionAmount : 0);
  };

  const handleCollapseOpen = () => {
    setOpen(!open);
  };

  return isLoading ? (
    <Loader />
  ) : (
    <Container>
      <Title>{t('transactions.detail.title')}</Title>
      <TransactionSection>
        <SubTitle>
          <div>{t('transactions.detail.saleAmountSectionTitle')}</div>
          <div>{moneyFormat(originalTransactionAmount)}</div>
        </SubTitle>
        <TransactionRow>
          <div>
            {`${
              transaction?.event_date && new Date(transaction.event_date).toLocaleDateString()
            } ${t('transactions.detail.transaction')} ${transaction?.sale_authorization_id}`}
          </div>
        </TransactionRow>
      </TransactionSection>
      {refunds?.length > 0 && (
        <TransactionSection>
          <SubTitle>
            <div>{t('transactions.detail.refundAmountSectionTitle')}</div>
            <div>{moneyFormat(Math.abs(totalRefundsAmount))}</div>
          </SubTitle>

          {visibleRefunds.map(({ authorization_id, event_date, gross_value }) => (
            <TransactionRow>
              <div>
                {`${event_date && new Date(event_date).toLocaleDateString()} ${t(
                  'transactions.detail.transaction'
                )} ${authorization_id}`}
              </div>
              <div>{moneyFormat(Math.abs(gross_value))}</div>
            </TransactionRow>
          ))}

          {hiddenRefunds.length > 0 && (
            <>
              <StyledCollapse in={open}>
                {hiddenRefunds.map(({ authorization_id, event_date, gross_value }) => (
                  <TransactionRow>
                    <div>
                      {`${event_date && new Date(event_date).toLocaleDateString()} ${t(
                        'transactions.detail.transaction'
                      )} ${authorization_id}`}
                    </div>
                    <div>{moneyFormat(Math.abs(gross_value))}</div>
                  </TransactionRow>
                ))}
              </StyledCollapse>
              <CollapseText onClick={handleCollapseOpen}>
                {open ? t('common.showLess') : t('common.showMore')}
                <StyledIcon icon="ic-arrow-down" color="gray700" open={open} />
              </CollapseText>
            </>
          )}
        </TransactionSection>
      )}
      <SubTitleOnly>
        <div>{t('transactions.detail.saleAvailableAmountTitle')}</div>
        <div>{moneyFormat(availableTransactionAmount)}</div>
      </SubTitleOnly>
      <ContainerInput>
        <Input
          id="refund_amount"
          name="refund_amount"
          testId="refund_amount"
          label={t('common.amount')}
          caption={errors.refund_amount || t('transactions.detail.subtitle')}
          value={formValues.refund_amount}
          onChange={(_event, value) => {
            handleInputChange({
              target: {
                value,
                id: 'refund_amount',
              },
            });
            setRefundAll('0');
          }}
          error={!!errors.refund_amount}
        />
      </ContainerInput>
      <StyledRadio
        name="refund_all"
        testId="refund_all"
        gridProps={{ xs: 12, item: true }}
        options={[{ value: '1', label: t('transactions.detail.refundAll') }]}
        onChange={() => {}}
        onClick={handleRefundAll}
        value={refundAll}
      />
      <ButtonList>
        <StyledButton
          gridProps={{ item: true }}
          id="btn-cancel"
          hide="mobile"
          variant="outlined"
          onClick={() => {
            history.push(Routes.TRANSACTIONS);
          }}
          type="outlined"
        >
          {t('paymentsQr.commercePortal.generateQR.buttonCancel')}
        </StyledButton>
        <StyledButton
          gridProps={{ item: true }}
          cardButton
          id="vincular-qr-vincular-botton"
          disabled={!!errors.refund_amount || !(formValues.refund_amount > 0)}
          onClick={handleRefund}
        >
          {t('transactions.detail.refound')}
        </StyledButton>
      </ButtonList>
      <ConfirmModal
        title={t('transactions.detail.errorBalanceSettledTitle')}
        subTitle={textErrorBalance}
        imageSourceUrl={`${publicPath}/warning.svg`}
        imageAlt="Warning"
        imageStyle={{ width: '92px', height: '86,3px', marginTop: '28px' }}
        open={errorBalance}
        textConfirm={t('transactions.detail.cancelErrorModal')}
        textCancel={null}
        handleConfirm={handleConfirm}
      />
    </Container>
  );
}

export default TransactionDetail;
